import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { expensesAPI, projectsAPI, usersAPI } from '../../services/api';
import { EXPENSE_CATEGORIES, EXPENSE_STATUSES } from '../../types/expense';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { DownloadIcon, RefreshIcon, ChartIcon } from '../../components/icons';

// ─── Types ───────────────────────────────────────────────────────────────────

type GroupBy = 'employee' | 'project' | 'category' | 'status';

interface ReportFilters {
  start_date: string;
  end_date: string;
  group_by: GroupBy;
  employee_id: string;
  project: { id: string; name: string } | null;
  category: string;
  status: string;
}

interface ReportRow {
  key: string;
  label: string;
  count: number;
  amount: number;
  percentage: number;
  breakdown: Record<string, { count: number; amount: number }>;
}

interface ReportData {
  rows: ReportRow[];
  grand_total: { count: number; amount: number };
  columns: string[];
  by_status?: Record<string, { count: number; amount: string }>;
}

interface UserOption {
  id: number;
  full_name: string;
}
interface ProjectOption {
  id: number;
  name: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
  }).format(amount);
}

function getTodayString(): string {
  return new Date().toISOString().slice(0, 10);
}

function getMonthStartString(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

/**
 * Normalise whatever the backend returns into a flat ReportData shape.
 * The backend may return either the raw ExpenseReport structure or
 * a pre-grouped structure — we handle both.
 */
function normaliseReportData(raw: any, groupBy: GroupBy): ReportData {
  if (!raw)
    return {
      rows: [],
      grand_total: { count: 0, amount: 0 },
      columns: [],
      by_status: {},
    };

  // If the backend already returns grouped rows use them directly.
  if (Array.isArray(raw.rows)) {
    const grandTotal = raw.grand_total ?? { count: 0, amount: 0 };
    return {
      rows: raw.rows.map((r: any) => ({
        key: String(r.key ?? r.id ?? r.label ?? ''),
        label: r.label ?? r.name ?? r.key ?? '',
        count: Number(r.count ?? 0),
        amount: parseFloat(r.amount ?? '0'),
        percentage: Number(r.percentage ?? 0),
        breakdown: r.breakdown ?? {},
      })),
      grand_total: {
        count: Number(grandTotal.count ?? 0),
        amount: parseFloat(grandTotal.amount ?? '0'),
      },
      columns: raw.columns ?? [],
      by_status: raw.by_status ?? {},
    };
  }

  // Fallback: convert ExpenseReport summary shape
  const summary = raw.summary ?? raw;
  const totalAmount = parseFloat(summary.total_amount ?? '0');
  const totalCount = Number(summary.total_count ?? 0);

  const byStatus = summary.by_status ?? {};
  const byEmployee = raw.by_employee ?? [];
  const byProject = raw.by_project ?? [];
  const byCategory = raw.by_category ?? [];

  let rows: ReportRow[] = [];
  let columns: string[] = [];

  if (groupBy === 'employee' && byEmployee.length > 0) {
    columns = EXPENSE_CATEGORIES.map((c) => c.value);
    rows = byEmployee.map((e: any) => {
      const amount = parseFloat(e.amount ?? '0');
      return {
        key: String(e.employee?.id ?? e.id ?? ''),
        label: e.employee?.full_name ?? e.name ?? 'Unknown',
        count: Number(e.count ?? 0),
        amount,
        percentage: totalAmount > 0 ? (amount / totalAmount) * 100 : 0,
        breakdown: e.by_category ?? {},
      };
    });
  } else if (groupBy === 'project' && byProject.length > 0) {
    columns = EXPENSE_CATEGORIES.map((c) => c.value);
    rows = byProject.map((p: any) => {
      const amount = parseFloat(p.amount ?? '0');
      return {
        key: String(p.project?.id ?? p.id ?? ''),
        label: p.project?.name ?? p.name ?? 'Unknown',
        count: Number(p.count ?? 0),
        amount,
        percentage: totalAmount > 0 ? (amount / totalAmount) * 100 : 0,
        breakdown: p.by_category ?? {},
      };
    });
  } else if (groupBy === 'category' && byCategory.length > 0) {
    columns = ['count', 'amount'];
    rows = byCategory.map((c: any) => {
      const amount = parseFloat(c.amount ?? '0');
      return {
        key: c.category ?? '',
        label:
          EXPENSE_CATEGORIES.find((ec) => ec.value === c.category)?.label ??
          c.category,
        count: Number(c.count ?? 0),
        amount,
        percentage: totalAmount > 0 ? (amount / totalAmount) * 100 : 0,
        breakdown: c.by_status ?? {},
      };
    });
  } else if (groupBy === 'status') {
    columns = EXPENSE_CATEGORIES.map((c) => c.value);
    rows = EXPENSE_STATUSES.map((s) => {
      const entry = byStatus[s.value] ?? { count: 0, amount: '0' };
      const amount = parseFloat(entry.amount ?? '0');
      return {
        key: s.value,
        label: s.label,
        count: Number(entry.count ?? 0),
        amount,
        percentage: totalAmount > 0 ? (amount / totalAmount) * 100 : 0,
        breakdown: {},
      };
    }).filter((r) => r.count > 0 || r.amount > 0);
  } else {
    // Generic fallback with whatever we have
    rows = [];
    columns = [];
  }

  return {
    rows,
    grand_total: { count: totalCount, amount: totalAmount },
    columns,
    by_status: byStatus,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function FilterSelect({
  label,
  value,
  onChange,
  options,
  isDark,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  isDark: boolean;
}) {
  const selectClass = `w-full rounded-xl px-3 py-2 text-sm border transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500/50 ${
    isDark
      ? 'bg-slate-800 border-slate-700 text-slate-200'
      : 'bg-white border-slate-300 text-slate-800'
  }`;
  return (
    <div className="flex flex-col gap-1">
      <label
        className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
      >
        {label}
      </label>
      <select
        className={selectClass}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function DateInput({
  label,
  value,
  onChange,
  isDark,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  isDark: boolean;
}) {
  const inputClass = `w-full rounded-xl px-3 py-2 text-sm border transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500/50 ${
    isDark
      ? 'bg-slate-800 border-slate-700 text-slate-200'
      : 'bg-white border-slate-300 text-slate-800'
  }`;
  return (
    <div className="flex flex-col gap-1">
      <label
        className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
      >
        {label}
      </label>
      <input
        type="date"
        className={inputClass}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function SkeletonTable({ isDark }: { isDark: boolean }) {
  const shimmer = isDark ? 'bg-slate-700/50' : 'bg-slate-200';
  return (
    <div className="space-y-2 p-4">
      {[...Array(6)].map((_, i) => (
        <div key={i} className={`h-10 rounded-lg ${shimmer} animate-pulse`} />
      ))}
    </div>
  );
}

// Mobile card for a single report row
function ReportCard({ row, isDark }: { row: ReportRow; isDark: boolean }) {
  const cardClass = isDark
    ? 'bg-slate-800/50 border border-slate-700/50'
    : 'bg-white border border-slate-200 shadow-sm';

  return (
    <div className={`${cardClass} rounded-xl p-4`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div
            className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {row.label}
          </div>
          <div
            className={`text-xs mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
          >
            {row.count} {row.count === 1 ? 'expense' : 'expenses'}
          </div>
        </div>
        <div className="text-right">
          <div
            className={`font-bold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {formatCurrency(row.amount)}
          </div>
          <div
            className={`text-xs mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
          >
            {row.percentage.toFixed(1)}%
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const ExpenseReports: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { showToast } = useToast();

  const [filters, setFilters] = useState<ReportFilters>(() => {
    const stored = sessionStorage.getItem('expenseReportFilters');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        /* ignore */
      }
    }
    return {
      start_date: getMonthStartString(),
      end_date: getTodayString(),
      group_by: 'employee' as GroupBy,
      employee_id: '',
      project: { id: '' },
      category: '',
      status: '',
    };
  });

  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sortCol, setSortCol] = useState<
    'label' | 'count' | 'amount' | 'percentage'
  >('amount');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [users, setUsers] = useState<UserOption[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);

  // Persist filters to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('expenseReportFilters', JSON.stringify(filters));
  }, [filters]);

  // Load filter dropdown options on mount
  useEffect(() => {
    usersAPI
      .getAll()
      .then((res) => {
        const data = res.data;
        setUsers(Array.isArray(data) ? data : (data?.users ?? []));
      })
      .catch(() => {});
    projectsAPI
      .getAll()
      .then((res) => {
        const data = res.data;
        setProjects(Array.isArray(data) ? data : (data?.projects ?? []));
      })
      .catch(() => {});
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        date_from: filters.start_date,
        date_to: filters.end_date,
        group_by: filters.group_by,
      };
      if (filters.employee_id) params.employee_id = filters.employee_id;
      if (filters.project?.id) params.project_id = filters.project.id;
      if (filters.category) params.category = filters.category;
      if (filters.status) params.status = filters.status;

      const res = await expensesAPI.getReports(params);
      // Unwrap the report from the response and map field names
      const reportData = res.data.report;
      const transformedData = {
        summary: reportData.summary,
        by_status: reportData.summary?.by_status || {},
        by_employee: reportData.employee || [],
        by_project: reportData.project || [],
        by_category: reportData.category || [],
      };
      setReportData(normaliseReportData(transformedData, filters.group_by));
    } catch {
      showToast(t('expense.report.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showToast, t]);

  // Auto-fetch on mount
  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params: Record<string, string> = {
        date_from: filters.start_date,
        date_to: filters.end_date,
        group_by: filters.group_by,
      };
      if (filters.employee_id) params.employee_id = filters.employee_id;
      if (filters.project?.id) params.project_id = filters.project.id;
      if (filters.category) params.category = filters.category;
      if (filters.status) params.status = filters.status;

      const res = await expensesAPI.exportCSV(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `expense-report-${filters.start_date}-${filters.end_date}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast(t('expense.report.exportCsv') + ' — OK', 'success');
    } catch {
      showToast(t('expense.report.exportFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (col: typeof sortCol) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  const sortedRows = reportData
    ? [...reportData.rows].sort((a, b) => {
        const mul = sortDir === 'asc' ? 1 : -1;
        if (sortCol === 'label') return mul * a.label.localeCompare(b.label);
        if (sortCol === 'count') return mul * (a.count - b.count);
        if (sortCol === 'percentage')
          return mul * (a.percentage - b.percentage);
        return mul * (a.amount - b.amount);
      })
    : [];

  // ── Derived styles ──
  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm'
    : 'bg-white border border-slate-200 shadow-sm';

  const thClass = `px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer select-none transition-colors ${
    isDark
      ? 'text-slate-400 hover:text-slate-200'
      : 'text-slate-500 hover:text-slate-700'
  }`;

  const tdClass = `px-5 py-4 text-sm whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-slate-700'}`;

  const SortIcon = ({ col }: { col: typeof sortCol }) => (
    <span className="ml-1 inline-block">
      {sortCol === col ? (
        sortDir === 'asc' ? (
          '↑'
        ) : (
          '↓'
        )
      ) : (
        <span className="opacity-30">↕</span>
      )}
    </span>
  );

  // ── Group-by options ──
  const groupByOptions: { value: GroupBy; label: string }[] = [
    { value: 'employee', label: t('expense.report.byEmployee') },
    { value: 'project', label: t('expense.report.byProject') },
    { value: 'category', label: t('expense.report.byCategory') },
    { value: 'status', label: t('expense.report.byStatus') },
  ];

  const employeeOptions = [
    { value: '', label: t('common.all', 'All') },
    ...users.map((u) => ({ value: String(u.id), label: u.full_name })),
  ];

  const projectOptions = [
    { value: '', label: t('common.all', 'All') },
    ...projects.map((p) => ({ value: String(p.id), label: p.name })),
  ];

  const categoryOptions = [
    { value: '', label: t('common.all', 'All') },
    ...EXPENSE_CATEGORIES.map((c) => ({ value: c.value, label: c.label })),
  ];

  const statusOptions = [
    { value: '', label: t('common.all', 'All') },
    ...EXPENSE_STATUSES.map((s) => ({ value: s.value, label: s.label })),
  ];

  const setFilter = <K extends keyof ReportFilters>(
    key: K,
    val: ReportFilters[K],
  ) => {
    setFilters((prev) => ({ ...prev, [key]: val }));
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-slate-950' : 'bg-slate-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1
              className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('expense.report.title')}
            </h1>
            <p
              className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
            >
              {filters.start_date} — {filters.end_date}
            </p>
          </div>
          <button
            onClick={handleExportCSV}
            disabled={exporting || loading}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:from-violet-500 hover:to-purple-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {Icons.download}
            {exporting
              ? t('common.loading', 'Loading…')
              : t('expense.report.exportCsv')}
          </button>
        </div>

        {/* ── Filters ── */}
        <div className={`${cardClass} rounded-2xl p-5`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <DateInput
              label={t('expense.report.dateFrom')}
              value={filters.start_date}
              onChange={(v) => setFilter('start_date', v)}
              isDark={isDark}
            />
            <DateInput
              label={t('expense.report.dateTo')}
              value={filters.end_date}
              onChange={(v) => setFilter('end_date', v)}
              isDark={isDark}
            />
            <FilterSelect
              label={t('expense.report.groupBy')}
              value={filters.group_by}
              onChange={(v) => setFilter('group_by', v as GroupBy)}
              options={groupByOptions}
              isDark={isDark}
            />
            <FilterSelect
              label={t('expense.filters.status')}
              value={filters.status}
              onChange={(v) => setFilter('status', v)}
              options={statusOptions}
              isDark={isDark}
            />
            <FilterSelect
              label={t('expense.filters.category')}
              value={filters.category}
              onChange={(v) => setFilter('category', v)}
              options={categoryOptions}
              isDark={isDark}
            />
            <div className="sm:col-span-2 lg:col-span-1 flex items-end">
              <button
                onClick={fetchReport}
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? Icons.spinner : Icons.refresh}
                {loading
                  ? t('common.loading', 'Loading…')
                  : t('expense.report.generateReport')}
              </button>
            </div>
          </div>

          {/* Second row: employee + project filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-dashed border-slate-700/30">
            <FilterSelect
              label={t('expense.filters.employee', 'Employee')}
              value={filters.employee_id}
              onChange={(v) => setFilter('employee_id', v)}
              options={employeeOptions}
              isDark={isDark}
            />
            <FilterSelect
              label={t('expense.filters.project')}
              value={filters.project?.id || ''}
              onChange={(v) => setFilter('project', { id: v, name: '' })}
              options={projectOptions}
              isDark={isDark}
            />
          </div>
        </div>

        {/* ── Charts ── */}
        {!loading && reportData && reportData.rows.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bar Chart */}
            <div className={`${cardClass} rounded-2xl p-6`}>
              <h3
                className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {t(
                  'expense.report.spendingBy',
                  `Spending by ${filters.group_by}`,
                )}
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={sortedRows.slice(0, 8).map((row) => ({
                    name:
                      typeof row.label === 'string'
                        ? row.label.substring(0, 15)
                        : row.label,
                    amount: Number(row.amount),
                    fullLabel: row.label,
                  }))}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={isDark ? '#334155' : '#e2e8f0'}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    stroke={isDark ? '#64748b' : '#94a3b8'}
                    fontSize={12}
                  />
                  <YAxis
                    stroke={isDark ? '#64748b' : '#94a3b8'}
                    fontSize={12}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? '#1e293b' : '#ffffff',
                      border: isDark
                        ? '1px solid #334155'
                        : '1px solid #e2e8f0',
                    }}
                    formatter={(value) => formatCurrency(value as number)}
                  />
                  <Bar dataKey="amount" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Pie Chart - Status Distribution */}
            {reportData.by_status && (
              <div className={`${cardClass} rounded-2xl p-6`}>
                <h3
                  className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {t(
                    'expense.report.statusDistribution',
                    'Status Distribution',
                  )}
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={Object.entries(reportData.by_status)
                        .filter(([_, data]) => data.count > 0)
                        .map(([status, data]) => ({
                          name:
                            status.charAt(0).toUpperCase() + status.slice(1),
                          value: parseFloat(data.amount),
                          count: data.count,
                        }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) =>
                        `${name}: ${formatCurrency(value)}`
                      }
                    >
                      <Cell fill="#22c55e" /> {/* approved - green */}
                      <Cell fill="#f59e0b" /> {/* pending - amber */}
                      <Cell fill="#ef4444" /> {/* rejected - red */}
                      <Cell fill="#64748b" /> {/* draft - slate */}
                      <Cell fill="#8b5cf6" /> {/* reimbursed - purple */}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatCurrency(value as number)}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* ── Report Table ── */}
        <div className={`${cardClass} rounded-2xl overflow-hidden`}>
          {loading ? (
            <SkeletonTable isDark={isDark} />
          ) : !reportData || reportData.rows.length === 0 ? (
            <div
              className={`p-16 text-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
            >
              <div
                className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}
              >
                {Icons.chart}
              </div>
              <p
                className={`text-base font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}
              >
                {t('expense.report.noData')}
              </p>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="md:hidden px-4 py-4 space-y-3">
                {sortedRows.map((row) => (
                  <ReportCard key={row.key} row={row} isDark={isDark} />
                ))}
                {/* Grand total card */}
                <div
                  className={`rounded-xl p-4 ${isDark ? 'bg-violet-900/30 border border-violet-700/40' : 'bg-violet-50 border border-violet-200'}`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`font-bold text-sm ${isDark ? 'text-violet-300' : 'text-violet-800'}`}
                    >
                      {t('expense.summary.total')}
                    </span>
                    <div className="text-right">
                      <div
                        className={`font-bold text-sm ${isDark ? 'text-violet-200' : 'text-violet-900'}`}
                      >
                        {formatCurrency(reportData.grand_total.amount)}
                      </div>
                      <div
                        className={`text-xs ${isDark ? 'text-violet-400' : 'text-violet-600'}`}
                      >
                        {reportData.grand_total.count}{' '}
                        {t('expense.report.count', 'expenses')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full">
                  <thead
                    className={`sticky top-0 z-10 ${isDark ? 'bg-slate-900' : 'bg-white'}`}
                  >
                    <tr
                      className={
                        isDark
                          ? 'border-b border-slate-700/50'
                          : 'border-b border-slate-200'
                      }
                    >
                      <th
                        className={thClass}
                        onClick={() => handleSort('label')}
                      >
                        {t('expense.report.name')}
                        <SortIcon col="label" />
                      </th>
                      <th
                        className={thClass}
                        onClick={() => handleSort('count')}
                      >
                        {t('expense.report.count')}
                        <SortIcon col="count" />
                      </th>
                      <th
                        className={thClass}
                        onClick={() => handleSort('amount')}
                      >
                        {t('expense.report.amount')}
                        <SortIcon col="amount" />
                      </th>
                      <th
                        className={thClass}
                        onClick={() => handleSort('percentage')}
                      >
                        {t('expense.report.percentOfTotal')}
                        <SortIcon col="percentage" />
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    className={
                      isDark
                        ? 'divide-y divide-slate-700/50'
                        : 'divide-y divide-slate-100'
                    }
                  >
                    {sortedRows.map((row, idx) => (
                      <tr
                        key={row.key}
                        className={`transition-colors ${
                          isDark
                            ? idx % 2 === 0
                              ? 'bg-slate-900/10 hover:bg-slate-800/40'
                              : 'bg-slate-800/20 hover:bg-slate-800/40'
                            : idx % 2 === 0
                              ? 'bg-white hover:bg-slate-50'
                              : 'bg-slate-50/60 hover:bg-slate-50'
                        }`}
                      >
                        <td
                          className={`${tdClass} font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
                        >
                          {row.label}
                        </td>
                        <td className={tdClass}>{row.count}</td>
                        <td className={`${tdClass} font-semibold`}>
                          {formatCurrency(row.amount)}
                        </td>
                        <td className={tdClass}>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 max-w-[80px] h-1.5 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-700">
                              <div
                                className="h-full rounded-full bg-violet-500"
                                style={{
                                  width: `${Math.min(row.percentage, 100)}%`,
                                }}
                              />
                            </div>
                            <span>{row.percentage.toFixed(1)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Footer / grand total */}
                  <tfoot>
                    <tr
                      className={`font-bold ${isDark ? 'bg-slate-800/60 border-t border-slate-600' : 'bg-slate-100 border-t border-slate-300'}`}
                    >
                      <td
                        className={`${tdClass} font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                      >
                        {t('expense.summary.total')}
                      </td>
                      <td
                        className={`${tdClass} font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                      >
                        {reportData.grand_total.count}
                      </td>
                      <td
                        className={`${tdClass} font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                      >
                        {formatCurrency(reportData.grand_total.amount)}
                      </td>
                      <td
                        className={`${tdClass} font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                      >
                        100%
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>

        {/* ── Summary cards ── */}
        {reportData && reportData.rows.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className={`${cardClass} rounded-2xl p-5`}>
              <div
                className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('expense.report.totalExpenses')}
              </div>
              <div
                className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {reportData.grand_total.count}
              </div>
            </div>
            <div className={`${cardClass} rounded-2xl p-5`}>
              <div
                className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('expense.report.totalAmount')}
              </div>
              <div className={`text-xl font-bold text-violet-500`}>
                {formatCurrency(reportData.grand_total.amount)}
              </div>
            </div>
            <div className={`${cardClass} rounded-2xl p-5`}>
              <div
                className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('expense.report.groupBy')}
              </div>
              <div
                className={`text-sm font-semibold capitalize ${isDark ? 'text-slate-200' : 'text-slate-700'}`}
              >
                {
                  groupByOptions.find((o) => o.value === filters.group_by)
                    ?.label
                }
              </div>
            </div>
            <div className={`${cardClass} rounded-2xl p-5`}>
              <div
                className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('expense.report.count')} {t(
                  `${filters.group_by}`,
                )}
              </div>
              <div
                className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {reportData.rows.length}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Icons ────────────────────────────────────────────────────────────────────

const Icons = {
  download: <DownloadIcon className="w-4 h-4" strokeWidth={2} />,
  refresh: <RefreshIcon className="w-4 h-4" strokeWidth={2} />,
  // Spinner uses animate-spin + opacity overlays unique to this file; kept inline.
  spinner: (
    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  ),
  chart: <ChartIcon className="w-8 h-8" strokeWidth={1.5} />,
};

export default ExpenseReports;
